import React, { useRef } from "react";
import {
  Row,
  Col,
  Layout,
  Typography,
  Form,
  Spin,
  Drawer,
  Grid,
  Select,
  message,
} from "antd";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getGenerateAnnouncement,
  resetAnnouncement,
  getAudioData,
  getHeygenData,
  getCaptionedNewsReelData,
  setAnnouncementLanguage,
} from "../../Redux/Slice/DataSlice";
import { useNavigate } from "react-router-dom";
import { StyledMainCol, StyledQuill } from "./styles";
import TextArea from "antd/es/input/TextArea";
import { StyledContent1 } from "../NewsList/Components/ArticleSidebar/style";
import { StyledPrimaryButton } from "./styles";
import {
  FileTextFilled,
  LoadingOutlined,
  MenuOutlined,
  VideoCameraFilled,
} from "@ant-design/icons";
import { useKeycloak } from "@react-keycloak/web";
import NoPermission from "../../NoPermission";
import { Quill } from "react-quill";
import { NewsSidebar } from "../NewsList/Components/NewsSidebar";

const { Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { Option } = Select;

export const Announcement = () => {
  const dispatch = useDispatch();
  const [announcementText, setAnnouncementText] = useState("");
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [formattedText, setFormattedText] = useState("");
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isDesktop = screens.lg || screens.xl || screens.xxl;
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [mediaType, setMediaType] = useState("video");
  const [videoModel, setVideoModel] = useState("avatar");
  const vidconfig = useSelector((state: any) => state.caster.selectedConfig);
  const selectedGender = vidconfig?.image === "Mcaster" ? "male" : "female";

  const handleDrawerOpen = () => {
    setDrawerVisible(true);
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
  };

  const { keycloak } = useKeycloak();

  const {
    response: generatedAnnouncement = [],
    initialAnnouncementLoad: announcementLoading,
    loading,
    error: announcementError,
    language: announcementLanguage = "english",
  } = useSelector((state: any) => state.data.generatedAnnouncement);

  const antIcon = (
    <LoadingOutlined
      style={{ fontSize: 30, color: "#005461", fontWeight: 600 }}
      spin
    />
  );

  const prevAnnouncementText = useRef("");
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (generatedAnnouncement && generatedAnnouncement !== "null") {
      setAnnouncementText(generatedAnnouncement);
    }
  }, [generatedAnnouncement]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (
        prevAnnouncementText.current !== announcementText &&
        announcementText.length > 50 &&
        keycloak.authenticated &&
        keycloak.token
      ) {
        console.log("Announcement streaming complete!");
      }
      prevAnnouncementText.current = announcementText;
    }, 2000);

    return () => clearTimeout(timeoutRef.current);
  }, [announcementText, keycloak.authenticated, keycloak.token, dispatch]);

  const handleChange = (value) => {
    setAnnouncementText(value);
    setFormattedText(value);

    // Update editor direction based on content
    const direction = isRTL(value) ? "rtl" : "ltr";
    const editor = document.querySelector(
      "#quill-content-announcement .ql-editor"
    ) as HTMLElement;
    if (editor) {
      editor.setAttribute("dir", direction);
      editor.style.textAlign = direction === "rtl" ? "right" : "left";
    }
  };

  const Font = Quill.import("formats/font");
  Font.whitelist = [
    "Arial",
    "Times New Roman",
    "Courier New",
    "Georgia",
    "Verdana",
    "Helvetica",
    "sans-serif",
    "serif",
    "monospace",
  ];
  Quill.register(Font, true);

  const handleBack = () => {
    dispatch(resetAnnouncement());
    setAnnouncementText("");
    localStorage.removeItem("announcementText");
    navigate("/NewsList");
  };

  const handleBackWithWarning = () => {
    const confirmLeave = window.confirm("Any changes made will be reverted!");
    if (confirmLeave) {
      setUnsavedChanges(false);
      handleBack();
    }
  };

  const handleBrowserBackButton = (event) => {
    if (unsavedChanges) {
      event.returnValue = "Any changes made will be reverted!";
    }
  };

  useEffect(() => {
    window.onbeforeunload = handleBrowserBackButton;

    return () => {
      window.onbeforeunload = null;
    };
  }, [unsavedChanges]);

  useEffect(() => {
    // Reset announcement state when component mounts
    dispatch(resetAnnouncement());

    const savedText = localStorage.getItem("announcementText");
    if (savedText) {
      setAnnouncementText(savedText);
      setFormattedText(savedText);
    }
  }, [dispatch]);

  useEffect(() => {
    if (`${announcementText}`) {
      localStorage.setItem("announcementText", `${announcementText}`);
    } else if (`${formattedText}`) {
      localStorage.setItem("formattedAnnouncementText", `${formattedText}`);
    }
  }, [`${announcementText}`, `${formattedText}`]);

  useEffect(() => {
    return () => {
      dispatch(resetAnnouncement());
      setAnnouncementText("");
      setFormattedText("");
      localStorage.removeItem("announcementText");
      localStorage.removeItem("formattedAnnouncementText");
    };
  }, [dispatch]);

  useEffect(() => {
    // Show error message when announcement generation fails
    if (announcementError && !announcementLoading) {
      message.error("Failed to generate announcement. Please try again.");
    }
  }, [announcementError, announcementLoading]);

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [textareaValue, setTextareaValue] = useState("");
  const [language, setLanguage] = useState(announcementLanguage);
  const [form] = Form.useForm();

  useEffect(() => {
    setLanguage(announcementLanguage);
  }, [announcementLanguage]);

  const handleLanguageSelection = (value: string) => {
    setLanguage(value);
    dispatch(setAnnouncementLanguage(value));
    setAnnouncementText("");
    setFormattedText("");
    setTextareaValue("");
    const promptText = form.getFieldValue("Prompt");
    setIsButtonDisabled(!promptText || promptText.trim() === "");
    localStorage.removeItem("announcementText");
    localStorage.removeItem("formattedAnnouncementText");
  };

  const isRTL = (text) => {
    const rtlCharPattern = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
    return rtlCharPattern.test(text);
  };

  useEffect(() => {
    // Update editor direction based on content language detection
    const editor = document.querySelector(
      "#quill-content-announcement .ql-editor"
    ) as HTMLElement;
    if (editor && announcementText) {
      const detectedRTL = isRTL(announcementText);
      editor.setAttribute("dir", detectedRTL ? "rtl" : "ltr");
      if (detectedRTL) {
        editor.style.textAlign = "right";
      } else {
        editor.style.textAlign = "left";
      }
    }
  }, [announcementText]);

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setTextareaValue(value);
    setIsButtonDisabled(value.trim() === "");
  };

  const handleGenerate = () => {
    const promptText = form.getFieldValue("Prompt");
    // Reset announcement state before generating new content
    dispatch(resetAnnouncement());

    const payload: any = {
      promptString: promptText,
      language: language,
    };

    // Only include announcement_text if there's existing content in Quill
    if (announcementText && announcementText.trim() !== "") {
      payload.announcement_text = announcementText;
    }

    dispatch(getGenerateAnnouncement({ payload, token: keycloak.token }));
    setIsButtonDisabled(true);
    setTextareaValue("");
    form.resetFields();
  };

  const removeHtmlTags = (text) => {
    return text.replace(/<[^>]*>/g, "");
  };

  const generateMedia = () => {
    const cleanText = removeHtmlTags(announcementText);

    if (mediaType === "audio") {
      dispatch(
        getAudioData({
          text: cleanText,
          voice: vidconfig?.voice, // ✅ voice_id from Redux
          token: keycloak.token,
          language,
        })
      );
    }

    if (mediaType === "video") {
      if (videoModel === "slideshow") {
        dispatch(
          getCaptionedNewsReelData({
            content: cleanText,
            language,
          })
        );
      } else {
        dispatch(
          getHeygenData({
            text: cleanText,
            avatar_id: vidconfig?.image, // ✅ avatar_id from Redux
            voice_id: vidconfig?.voice, // ✅ voice_id from Redux
            token: keycloak.token,
          })
        );
      }
    }

    setUnsavedChanges(false);
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: Font.whitelist }],
      [{ size: ["small", false, "large", "huge"] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ script: "sub" }, { script: "super" }],
      ["blockquote", "code-block"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      [{ direction: "rtl" }],
      [{ align: [] }],
      ["link", "image", "video"],
      ["clean"],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "blockquote",
    "code-block",
    "list",
    "bullet",
    "indent",
    "direction",
    "align",
    "link",
    "image",
    "video",
  ];

  return keycloak.authenticated ? (
    <StyledMainCol lg={{ span: 18, offset: 3 }}>
      <Row
        justify={"space-between"}
        style={{ minHeight: "100%", borderRadius: "5px" }}
      >
        <Col lg={15} xs={24}>
          <Content
            style={{
              minHeight: "100%",
              maxHeight: "100%",
              background: "white",
              borderRadius: "10px",
              borderRight: "10px solid whitesmoke",
              marginLeft: !isDesktop ? "10px" : "0",
            }}
          >
            {!isDesktop && (
              <StyledPrimaryButton
                icon={<MenuOutlined />}
                type="primary"
                onClick={handleDrawerOpen}
                style={{
                  display: "block",
                  marginBottom: "5px",
                  borderRadius: "5px",
                  width: "100%",
                }}
              >
                Media Generation
              </StyledPrimaryButton>
            )}

            <Row
              align={"middle"}
              style={{ borderBottom: "0.5px solid lightgrey", height: "60px" }}
            >
              <Title
                style={{
                  fontSize: "18px",
                  marginLeft: "20px",
                  display: "flex",
                  alignItems: "center",
                  color: "#005461",
                }}
              >
                <FileTextFilled
                  style={{
                    fontSize: "14px",
                    marginRight: "5px",
                    color: "#005461",
                  }}
                />
                Announcement
              </Title>
            </Row>
            <Content
              style={{
                background: "white",
                borderRadius: "12px",
                border: "1px solid #e1e8ed",
                padding: "16px",
                marginTop: "20px",
                marginBottom: "10px",
              }}
            >
              <Title style={{ fontSize: "20px", color: "#005461" }}>
                Announcement Generation:
              </Title>
              <Text style={{ color: "red" }}>
                Note: Please provide a relevant prompt for accurate generation.
              </Text>
              <Form form={form} layout="vertical">
                <Form.Item
                  style={{ marginTop: "5px", marginBottom: "10px" }}
                  name="Prompt"
                >
                  <TextArea
                    rows={2}
                    style={{ fontFamily: "Inter" }}
                    placeholder="Write a prompt to generate the announcement:"
                    maxLength={2000}
                    autoSize={{ minRows: 3, maxRows: 3 }}
                    onChange={handleDescriptionChange}
                  />
                </Form.Item>
                <StyledContent1
                  style={{
                    marginRight: 0,
                    marginTop: "20px",
                    marginBottom: "0px",
                    justifyContent: "flex-end",
                    gap: "12px",
                    alignItems: "flex-end",
                  }}
                >
                  <Form.Item
                    style={{
                      marginTop: "0px",
                      marginBottom: "0px",
                      flex: 1,
                      marginRight: "12px",
                    }}
                    label={
                      <span
                        style={{
                          width: isDesktop ? "50%" : "100%",
                          fontFamily: "Inter",
                        }}
                      >
                        Language
                      </span>
                    }
                  >
                    <Select
                      value={language}
                      onChange={handleLanguageSelection}
                      style={{
                        width: isDesktop ? "50%" : "100%",
                        fontFamily: "Inter",
                      }}
                    >
                      <Option value="english">English</Option>
                      <Option value="arabic">Arabic</Option>
                      <Option value="urdu">Urdu</Option>
                    </Select>
                  </Form.Item>
                  <StyledPrimaryButton
                    style={{
                      width: isDesktop ? "30%" : "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      height: "40px",
                    }}
                    type="primary"
                    onClick={handleGenerate}
                    disabled={isButtonDisabled || announcementLoading}
                  >
                    Create
                  </StyledPrimaryButton>
                </StyledContent1>
              </Form>
            </Content>

            <Row
              style={{ width: "100%", marginTop: "30px", minHeight: "100%" }}
            >
              {announcementLoading ? (
                <Content style={{ marginTop: "250px" }}>
                  <Row justify={"center"} align={"middle"}>
                    <Spin style={{ fontSize: "30px" }} indicator={antIcon} />
                    <Text
                      style={{
                        fontSize: "30px",
                        marginLeft: 10,
                        color: "#005461",
                        fontFamily: "Lato",
                        fontWeight: 600,
                      }}
                    >
                      Generating Announcement
                    </Text>
                  </Row>
                </Content>
              ) : (
                <Col span={24}>
                  <div
                    id="quill-content-announcement"
                    style={{
                      background: "white",
                      borderRadius: "12px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                      border: "1px solid #e1e8ed",
                      overflow: "hidden",
                    }}
                  >
                    <StyledQuill
                      value={`${announcementText}`}
                      onChange={handleChange}
                      modules={modules}
                      formats={formats}
                      theme="snow"
                      defaultValue="<p style='font-family: Arial;'>Start typing...</p>"
                    />
                  </div>
                </Col>
              )}
            </Row>
          </Content>
        </Col>

        <Col
          lg={9}
          xs={0}
          style={{
            minHeight: "100%",
            background: "white",
            borderRadius: "5px",
          }}
        >
          <Row justify={"center"}>
            <Content
              style={{
                background: "white",
                borderRadius: "12px",
                border: "1px solid #e1e8ed",
                padding: "16px",
                marginTop: "20px",
                marginBottom: "20px",
                width: "100%",
              }}
            >
              <NewsSidebar
                mediaType={mediaType}
                setMediaType={setMediaType}
                generateMedia={generateMedia}
                setUnsavedChanges={setUnsavedChanges}
                unsavedChanges={unsavedChanges}
                editedText={announcementText}
                handleBack={handleBack}
                videoModel={videoModel}
                setVideoModel={setVideoModel}
                hideBackButton
              />
            </Content>
          </Row>
        </Col>

        <Drawer
          title={
            <>
              <VideoCameraFilled
                style={{
                  fontSize: "16px",
                  marginRight: "3px",
                  color: "#005461",
                }}
              />
              Media Sidebar
            </>
          }
          placement="right"
          onClose={handleDrawerClose}
          visible={drawerVisible}
        >
          <NewsSidebar
            mediaType={mediaType}
            setMediaType={setMediaType}
            generateMedia={generateMedia}
            setUnsavedChanges={setUnsavedChanges}
            unsavedChanges={unsavedChanges}
            editedText={announcementText}
            handleBack={handleBack}
            videoModel={videoModel}
            setVideoModel={setVideoModel}
            hideBackButton
          />
        </Drawer>
      </Row>
    </StyledMainCol>
  ) : (
    <NoPermission />
  );
};

export default Announcement;
